import * as THREE from "three";
import {
  CORNERS,
  HALF_L,
  HALF_W,
  MERIDIAN_NORTH,
  MERIDIAN_SOUTH,
  PROPERTY_LENGTH,
  PROPERTY_WIDTH,
  SOUTH_WORD,
  type CornerId,
} from "./config";
import { makeLabel } from "./labels";

const FILL = 0x1a6b62;
const EDGE = 0x5ee0c8;
const MERIDIAN = 0xe24b4b;
const POST = 0xf2efe6;

export function buildProperty(): THREE.Group {
  const group = new THREE.Group();
  group.name = "property";

  const fill = new THREE.Mesh(
    new THREE.PlaneGeometry(PROPERTY_WIDTH, PROPERTY_LENGTH),
    new THREE.MeshStandardMaterial({
      color: FILL,
      transparent: true,
      opacity: 0.22,
      roughness: 0.9,
      metalness: 0.04,
      depthWrite: false,
    }),
  );
  fill.rotation.x = -Math.PI / 2;
  fill.position.y = 0.07;
  fill.receiveShadow = true;
  group.add(fill);
  group.add(meterGrid());

  const outline = new THREE.LineLoop(
    new THREE.BufferGeometry().setFromPoints([
      new THREE.Vector3(CORNERS.A.x, 0.12, CORNERS.A.z),
      new THREE.Vector3(CORNERS.B.x, 0.12, CORNERS.B.z),
      new THREE.Vector3(CORNERS.D.x, 0.12, CORNERS.D.z),
      new THREE.Vector3(CORNERS.C.x, 0.12, CORNERS.C.z),
    ]),
    new THREE.LineBasicMaterial({ color: EDGE }),
  );
  group.add(outline);

  const meridian = new THREE.Line(
    new THREE.BufferGeometry().setFromPoints([
      new THREE.Vector3(MERIDIAN_SOUTH.x, 0.13, MERIDIAN_SOUTH.z),
      new THREE.Vector3(MERIDIAN_NORTH.x, 0.13, MERIDIAN_NORTH.z),
    ]),
    new THREE.LineBasicMaterial({ color: MERIDIAN }),
  );
  meridian.name = "meridian";
  group.add(meridian);

  const southWord = makeLabel("SOUTH", "cardinal-word");
  southWord.position.set(SOUTH_WORD.x, 3.4, SOUTH_WORD.z);
  group.add(southWord);

  const ids: CornerId[] = ["A", "B", "C", "D"];
  for (const id of ids) {
    group.add(cornerPost(id));
  }

  const widthTag = makeLabel(`${PROPERTY_WIDTH} m`, "dim-label");
  widthTag.position.set(0, 2.2, HALF_L);
  group.add(widthTag);

  const lengthTag = makeLabel(`${PROPERTY_LENGTH} m`, "dim-label");
  lengthTag.position.set(HALF_W + 4.5, 2.2, 0);
  group.add(lengthTag);

  return group;
}

function meterGrid(): THREE.LineSegments {
  const pts: THREE.Vector3[] = [];
  const y = 0.09;
  const x0 = -HALF_W;
  const x1 = HALF_W;
  const z0 = -HALF_L;
  const z1 = HALF_L;
  for (let x = Math.ceil(x0); x < x1; x += 1) {
    pts.push(new THREE.Vector3(x, y, z0), new THREE.Vector3(x, y, z1));
  }
  for (let z = Math.ceil(z0); z < z1; z += 1) {
    pts.push(new THREE.Vector3(x0, y, z), new THREE.Vector3(x1, y, z));
  }
  return new THREE.LineSegments(
    new THREE.BufferGeometry().setFromPoints(pts),
    new THREE.LineBasicMaterial({ color: 0x3d8f86, transparent: true, opacity: 0.45 }),
  );
}

function cornerPost(id: CornerId): THREE.Group {
  const { x, z } = CORNERS[id];
  const g = new THREE.Group();
  g.name = `corner-${id}`;
  g.position.set(x, 0, z);

  const stake = new THREE.Mesh(
    new THREE.CylinderGeometry(0.22, 0.28, 2.2, 10),
    new THREE.MeshStandardMaterial({ color: POST, roughness: 0.45, metalness: 0.12 }),
  );
  stake.position.y = 1.1;
  stake.castShadow = true;
  g.add(stake);

  const cap = new THREE.Mesh(
    new THREE.CylinderGeometry(0.32, 0.32, 0.12, 12),
    new THREE.MeshStandardMaterial({ color: id === "A" || id === "D" ? MERIDIAN : EDGE, roughness: 0.4 }),
  );
  cap.position.y = 2.24;
  g.add(cap);

  const label = makeLabel(id, "corner-label");
  label.position.set(0, 3.1, 0);
  g.add(label);
  return g;
}
